import React, { useState, useEffect, useRef } from 'react';
import './MarketingDigital.css';
import Header from '../Header/Header';
import Footer from '../footer/footer';
import { motion, AnimatePresence } from 'framer-motion';
import TopAnnouncementBar from '../TopAnnouncementBar/TopAnnouncementBar';
import WhatsAppButton from '../whatsapp/WhatsAppButton';
import { Link } from 'react-router-dom';
import { 
  FaRocket, 
  FaChartLine, 
  FaSearch, 
  FaAd, 
  FaBullseye,
  FaFacebook,
  FaGoogle,
  FaInstagram,
  FaVideo,
  FaEnvelope,
  FaArrowRight,
  FaCheckCircle,
  FaAward,
  FaClock,
  FaCode,
  FaUsers,
  FaShieldAlt,
  FaRegEye,
  FaTachometerAlt
} from 'react-icons/fa';
import { 
  SiTiktok,
  SiLinkedin,
  SiGoogleanalytics
} from 'react-icons/si';
import { MdAutoAwesome, MdOutlineSpeed, MdOutlineSecurity, MdAnalytics, MdTrendingUp, MdCampaign } from 'react-icons/md';
import { GiAstronautHelmet, GiStarsStack, GiGalaxy, GiSpiralShell, GiCrystalBall, GiCyberEye, GiArtificialIntelligence, GiThunderSkull, GiBrain, GiRocketThruster, GiMagnifyingGlass, GiTargeting } from 'react-icons/gi';

const MarketingDigital = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isCursorActive, setIsCursorActive] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const sectionRef = useRef(null);
  let cursorTimeout;

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
      setIsCursorActive(true);
      
      clearTimeout(cursorTimeout);
      cursorTimeout = setTimeout(() => setIsCursorActive(false), 100);
    };
    
    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const scrollPercent = Math.min(1, Math.max(0, -rect.top / (rect.height - window.innerHeight)));
        setScrollProgress(scrollPercent);
      }
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(cursorTimeout);
    };
  }, []);

  const solutionsData = [
    {
      title: "Stratégie Digital Complète",
      tagline: "Vision 360°",
      description: "Plan d'action sur-mesure pour maximiser votre présence en ligne et vos conversions.",
      features: ["Audit digital complet", "Définition des personas", "Plan média digital", "KPI de performance"],
      icon: <FaChartLine />,
      color: "#3B82F6",
      stat: "ROI x3 en 6 mois"
    },
    {
      title: "Publicité en Ligne (SEA)",
      tagline: "Visibilité immédiate",
      description: "Campagnes publicitaires performantes sur Google Ads et réseaux sociaux pour générer du trafic qualifié.",
      features: ["Recherche mots-clés", "Création de campagnes", "Landing pages dédiées", "Suivi et optimisation"],
      icon: <FaGoogle />,
      color: "#10B981",
      stat: "+300% de conversions"
    },
    {
      title: "Référencement Naturel (SEO)",
      tagline: "Visibilité durable",
      description: "Optimisation pour améliorer votre positionnement dans les résultats de recherche et attirer du trafic organique.",
      features: ["Audit technique SEO", "Optimisation contenu", "Netlinking stratégique", "Reporting mensuel"],
      icon: <GiMagnifyingGlass />,
      color: "#8B5CF6",
      stat: "+150% de trafic"
    },
    {
      title: "Social Media Marketing",
      tagline: "Engagez votre audience",
      description: "Gestion et animation de vos réseaux sociaux pour développer votre communauté et votre notoriété.",
      features: ["Stratégie de contenu", "Création graphique", "Planning éditorial", "Analyse des performances"],
      icon: <FaInstagram />,
      color: "#EC4899",
      stat: "x4 plus d'engagement"
    },
    {
      title: "Email Marketing",
      tagline: "Communication directe",
      description: "Campagnes email automatisées pour convertir et fidéliser vos clients avec un ROI exceptionnel.",
      features: ["Stratégie de flux", "Design responsive", "Segmentation liste", "Analyse A/B testing"],
      icon: <FaEnvelope />,
      color: "#F59E0B",
      stat: "4200% de ROI moyen"
    },
    {
      title: "Content Marketing",
      tagline: "Attirez et convertissez",
      description: "Contenu de qualité pour attirer, engager et convertir votre audience en clients fidèles.",
      features: ["Stratégie éditoriale", "Création de contenu", "Optimisation SEO", "Distribution multi-canal"],
      icon: <FaVideo />,
      color: "#06B6D4",
      stat: "3x plus de leads"
    }
  ];

  const benefits = [
    { icon: <FaChartLine />, title: "Approche Data-Driven", description: "Décisions basées sur les données", color: "#3B82F6" },
    { icon: <MdCampaign />, title: "Expertise Multicanale", description: "Maîtrise de tous les leviers", color: "#10B981" },
    { icon: <FaAd />, title: "Publicité Performante", description: "ROI maximisé", color: "#8B5CF6" },
    { icon: <FaBullseye />, title: "Résultats Concrets", description: "KPI transparents", color: "#F59E0B" },
    { icon: <MdTrendingUp />, title: "Croissance Durable", description: "Stratégie long terme", color: "#EC4899" },
    { icon: <FaUsers />, title: "Audience Qualifiée", description: "Leads prêts à acheter", color: "#06B6D4" }
  ];

  const statsData = [
    { value: "300%", label: "ROI moyen", icon: <FaRocket /> },
    { value: "+50%", label: "trafic", icon: <FaTachometerAlt /> },
    { value: "95%", label: "satisfaction", icon: <FaAward /> },
    { value: "24/7", label: "suivi", icon: <FaClock /> }
  ];

  const technologies = [
    { icon: <FaGoogle />, name: "Google Ads", color: "#4285F4" },
    { icon: <FaFacebook />, name: "Facebook Ads", color: "#1877F2" },
    { icon: <FaInstagram />, name: "Instagram", color: "#E4405F" },
    { icon: <SiLinkedin />, name: "LinkedIn Ads", color: "#0A66C2" },
    { icon: <SiTiktok />, name: "TikTok Ads", color: "#000000" },
    { icon: <SiGoogleanalytics />, name: "Google Analytics", color: "#F9AB00" }
  ];

  const getPreviewBadgeText = () => {
    switch(activeTab) {
      case 0: return "Stratégie 360°";
      case 1: return "Performance garantie";
      case 2: return "Visibilité durable";
      case 3: return "Community management";
      case 4: return "Automatisation email";
      case 5: return "Content roi";
      default: return "Solution marketing";
    }
  };

  return (
    <div className="mk-container" ref={sectionRef}>
      <WhatsAppButton />
      <TopAnnouncementBar />
      <Header />
      
      {/* Cursor Glow */}
      <motion.div 
        className={`mk-cursor-glow ${isCursorActive ? 'active' : ''}`}
        animate={{
          x: mousePosition.x - 300,
          y: mousePosition.y - 300,
        }}
        transition={{ type: "spring", damping: 30, stiffness: 200, mass: 0.5 }}
      />

      {/* Particles */}
      <div className="mk-particles">
        {[...Array(60)].map((_, i) => (
          <div key={i} className="mk-particle" style={{
            left: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 20}s`,
            animationDuration: `${20 + Math.random() * 30}s`,
            width: `${1.5 + Math.random() * 3}px`,
            height: `${1.5 + Math.random() * 3}px`,
          }} />
        ))}
      </div>

      {/* Animated Orbs */}
      <div className="mk-orbs">
        <div className="mk-orb mk-orb-1" style={{ transform: `translate(${scrollProgress * 100}px, ${scrollProgress * 50}px)` }} />
        <div className="mk-orb mk-orb-2" style={{ transform: `translate(-${scrollProgress * 80}px, -${scrollProgress * 60}px)` }} />
        <div className="mk-orb mk-orb-3" style={{ transform: `translate(${scrollProgress * 60}px, -${scrollProgress * 40}px)` }} />
        <div className="mk-orb mk-orb-4" style={{ transform: `translate(-${scrollProgress * 70}px, ${scrollProgress * 70}px)` }} />
      </div>

      {/* Floating Elements */}
      <div className="mk-floating">
        <GiGalaxy className="mk-float mk-float-1" />
        <GiSpiralShell className="mk-float mk-float-2" />
        <GiAstronautHelmet className="mk-float mk-float-3" />
        <GiStarsStack className="mk-float mk-float-4" />
        <GiCrystalBall className="mk-float mk-float-5" />
        <GiCyberEye className="mk-float mk-float-6" />
        <GiArtificialIntelligence className="mk-float mk-float-7" />
        <GiThunderSkull className="mk-float mk-float-8" />
        <GiBrain className="mk-float mk-float-9" />
        <GiRocketThruster className="mk-float mk-float-10" />
        <GiTargeting className="mk-float mk-float-11" />
        <GiMagnifyingGlass className="mk-float mk-float-12" />
      </div>
      
      {/* ========== HERO BANNER ========== */}
      <section className="mk-hero">
        <div className="mk-hero-overlay"></div>
        <div className="mk-hero-inner">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mk-hero-badge"
          >
            <GiTargeting />
            <span>Marketing Digital Excellence</span>
            <GiStarsStack />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mk-hero-title"
          >
            Marketing digital performant<br />
            <span className="mk-gradient-text">qui convertit</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mk-hero-subtitle"
          >
            Stratégies digitales sur-mesure pour augmenter votre visibilité, générer des leads qualifiés 
            et booster vos ventes en ligne.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mk-hero-buttons"
          >
            <Link to="/reservation-appel">
              <button className="mk-btn mk-btn-primary">
                <MdAutoAwesome /> Démarrer mon projet <FaArrowRight />
              </button>
            </Link>
            <Link to="/realisations">
              <button className="mk-btn mk-btn-secondary">
                <FaCode /> Voir nos résultats
              </button>
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mk-hero-stats"
          >
            {statsData.map((stat, idx) => (
              <div key={idx} className="mk-stat-card">
                <div className="mk-stat-icon">{stat.icon}</div>
                <div className="mk-stat-value">{stat.value}</div>
                <div className="mk-stat-label">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ========== BENEFITS SECTION ========== */}
      <section className="mk-benefits">
        <div className="mk-container-inner">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mk-section-header"
          >
            <div className="mk-badge">Pourquoi nous</div>
            <h2>Une approche <span className="mk-gradient-text">data-driven</span></h2>
            <p>Des résultats mesurables et un retour sur investissement optimal</p>
          </motion.div>

          <div className="mk-benefits-grid">
            {benefits.map((benefit, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                whileHover={{ y: -8 }}
                className="mk-benefit-card"
                style={{ '--mk-card-color': benefit.color }}
              >
                <div className="mk-benefit-icon" style={{ color: benefit.color }}>{benefit.icon}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
                <div className="mk-benefit-glow" style={{ background: `radial-gradient(circle, ${benefit.color}20, transparent)` }} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== SOLUTIONS SECTION - TABS ========== */}
      <section className="mk-solutions">
        <div className="mk-container-inner">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mk-section-header"
          >
            <div className="mk-badge">Nos services</div>
            <h2>Solutions de <span className="mk-gradient-text">marketing digital</span></h2>
            <p>Des solutions complètes pour chaque étape de votre parcours client</p>
          </motion.div>

          {/* Tabs navigation */}
          <div className="mk-tabs-nav">
            {solutionsData.map((sol, idx) => (
              <button
                key={idx}
                className={`mk-tab-btn ${activeTab === idx ? 'active' : ''}`}
                onClick={() => setActiveTab(idx)}
                style={{ '--mk-tab-color': sol.color }}
              >
                <span className="mk-tab-icon">{sol.icon}</span>
                <span className="mk-tab-title">{sol.title}</span>
              </button>
            ))}
          </div>

          {/* Tab content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="mk-tab-content"
            >
              <div className="mk-tab-left">
                <div className="mk-solution-tagline" style={{ color: solutionsData[activeTab].color }}>
                  {solutionsData[activeTab].tagline}
                </div>
                <h3>{solutionsData[activeTab].title}</h3>
                <p>{solutionsData[activeTab].description}</p>
                <div className="mk-features-list">
                  {solutionsData[activeTab].features.map((feat, i) => (
                    <div key={i} className="mk-feature-item">
                      <FaCheckCircle style={{ color: solutionsData[activeTab].color }} />
                      <span>{feat}</span>
                    </div>
                  ))}
                </div>
                <div className="mk-solution-stat" style={{ background: `${solutionsData[activeTab].color}15`, borderLeftColor: solutionsData[activeTab].color }}>
                  <FaRocket />
                  <span><strong>{solutionsData[activeTab].stat}</strong></span>
                </div>
              </div>
              <div className="mk-tab-right">
                <div className="mk-preview-card" style={{ background: `linear-gradient(135deg, ${solutionsData[activeTab].color}10, rgba(10,17,40,0.4))` }}>
                  <div className="mk-preview-icon" style={{ color: solutionsData[activeTab].color }}>
                    {solutionsData[activeTab].icon}
                  </div>
                  <div className="mk-preview-badge">
                    {getPreviewBadgeText()}
                  </div>
                  <div className="mk-preview-stats">
                    <span>📊 Data-driven</span>
                    <span>🎯 Ciblé</span>
                    <span>📈 ROI prouvé</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ========== TECHNOLOGIES SECTION ========== */}
      <section className="mk-tech">
        <div className="mk-container-inner">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mk-section-header"
          >
            <div className="mk-badge">Canaux maîtrisés</div>
            <h2>Plateformes et <span className="mk-gradient-text">outils digitaux</span></h2>
            <p>Nous maîtrisons l'ensemble des canaux pour une stratégie intégrée</p>
          </motion.div>

          <div className="mk-tech-grid">
            {technologies.map((tech, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                whileHover={{ y: -5 }}
                className="mk-tech-card"
              >
                <div className="mk-tech-icon" style={{ color: tech.color }}>{tech.icon}</div>
                <h4>{tech.name}</h4>
                <div className="mk-tech-bar">
                  <div className="mk-tech-progress" style={{ backgroundColor: tech.color }} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== FINAL CTA ========== */}
      <section className="mk-final-cta">
        <div className="mk-cta-pattern"></div>
        <div className="mk-cta-inner">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mk-cta-icon"
          >
            <GiRocketThruster />
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Prêt à <span className="mk-gradient-text">booster</span> votre business en ligne ?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            Contactez-nous pour un audit gratuit de votre présence digitale.
          </motion.p>
          <Link to="/reservation-appel">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="mk-cta-button"
            >
              <MdAutoAwesome /> Demander un audit gratuit <FaArrowRight />
            </motion.button>
          </Link>
          <div className="mk-cta-features">
            <div><FaCheckCircle /> Réponse sous 24h</div>
            <div><FaShieldAlt /> Garantie satisfait</div>
            <div><FaUsers /> Support dédié</div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MarketingDigital;